import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Braces,
  ClipboardCopy,
  Copy,
  Download,
  Layers,
  ListOrdered,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { cn } from '../../poker-ui/utils/cn';
import { legalPlays } from '../../shared/game/belot/armenian/tricks';
import { ARMENIAN_BELOT_LOCAL_UI_ANIMATIONS } from './armenianBelotLocalUiAnimationsReference';

const { buildPublicSnapshot } = require('../../shared/game/belot/armenian/snapshot');
const { serializeArmenianEngineStateForLog } = require('../../game/belotArmenian/debugSerialize');

const RANK_TO_PLAYING = ['7', '8', '9', 'J', 'Q', 'K', '10', 'A'];
const SUIT_LETTER = ['C', 'D', 'H', 'S'];

/** @param {number} cardId */
function cardIdLabel(cardId) {
  if (!Number.isInteger(cardId)) return String(cardId);
  const suitIdx = Math.floor(cardId / 8);
  const rankIdx = cardId % 8;
  const s = SUIT_LETTER[suitIdx] ?? '?';
  const r = RANK_TO_PLAYING[rankIdx] ?? '?';
  return `${r}${s}`;
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {unknown} props.engineState — живое состояние из движка (в т.ч. `rng`; в JSON не попадает)
 * @param {Array<{
 *   seq: number,
 *   at: string,
 *   cmd: unknown,
 *   events: unknown[],
 *   eventSummaries?: Array<{ type: string, payloadKeys: string[] }>,
 *   diff?: Record<string, unknown>|null,
 *   rng?: Record<string, unknown>,
 *   ui?: { triggeredAnimationIds?: string[] },
 *   validation?: Record<string, unknown>,
 *   stateAfter: Record<string, unknown>|null,
 *   ok: boolean,
 *   error?: string,
 * }>} props.history
 * @param {() => void} props.onClearHistory
 * @param {Array<Record<string, unknown>>} [props.scoreSheetRows]
 * @param {unknown} [props.lastRoundSummary]
 */
export function ArmenianBelotEngineDebugPanel({
  open,
  onClose,
  engineState,
  history,
  onClearHistory,
  scoreSheetRows = [],
  lastRoundSummary = null,
}) {
  const [tab, setTab] = useState('state');
  const [copyNotice, setCopyNotice] = useState(/** @type {null | 'engine' | 'full'} */ (null));
  const [expandedSeq, setExpandedSeq] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const stateJson = useMemo(
    () => JSON.stringify(serializeArmenianEngineStateForLog(engineState) ?? {}, null, 2),
    [engineState],
  );

  const publicSnapshotJson = useMemo(() => {
    try {
      const snap = buildPublicSnapshot(engineState, {
        handsVisible: 'debug_all',
        lastEvents: [],
        roundScoreSheet: [],
      });
      return JSON.stringify(snap, null, 2);
    } catch (e) {
      return JSON.stringify({ error: e instanceof Error ? e.message : String(e) }, null, 2);
    }
  }, [engineState]);

  const humanLegalPlays = useMemo(() => {
    if (!engineState || typeof engineState !== 'object') return null;
    if (engineState.phase !== 'play') return null;
    if (engineState.currentTurnSeat !== 0) return null;
    const c = engineState.contract;
    const hand = engineState.hands?.[0];
    const lead = engineState.trickLeadSeat;
    const trick = engineState.currentTrick;
    if (!c || !Array.isArray(hand) || lead == null) return null;
    try {
      const ids = legalPlays(hand, trick, c.trumpSuit, lead);
      return ids.map((id) => ({ id, label: cardIdLabel(id) }));
    } catch {
      return [];
    }
  }, [engineState]);

  /** Единый развёрнутый документ для специалистов: все разделы, без сокращений (indent 2). */
  const specialistFullReport = useMemo(() => {
    let publicSnapshot;
    try {
      publicSnapshot = buildPublicSnapshot(engineState, {
        handsVisible: 'debug_all',
        lastEvents: [],
        roundScoreSheet: [],
      });
    } catch (e) {
      publicSnapshot = { _buildPublicSnapshotError: e instanceof Error ? e.message : String(e) };
    }

    const rngSession =
      engineState &&
      typeof engineState === 'object' &&
      engineState.rngMeta &&
      typeof engineState.rngMeta === 'object' &&
      typeof engineState.rngMeta.seed === 'number'
        ? { seed: engineState.rngMeta.seed >>> 0, algorithm: 'mulberry32' }
        : { algorithm: 'opaque', note: 'нет rngMeta (кастомный rng или старый формат)' };

    return {
      reportVersion: 3,
      exportedAt: new Date().toISOString(),
      source: 'BelotArmenianLocalLobby',
      description_ru:
        'Полная сводка для специалиста (v3): engine_state_full с mulberry32 rng (seed, draws), rng_session с стартовым seed сессии, DTO (buildPublicSnapshot), история шагов с diff состояний, eventSummaries, снимок RNG после шага, ui.triggeredAnimationIds (эвристика по cmd/events), validation (legalMovesSnapshot для PLAY_CARD и код отклонённых команд), без полного stateAfter на каждом шаге (экономия размера; актуальное состояние — в engine_state_full). Плюс листок UI, итог раунда, допустимые ходы места 0, справочник анимаций.',
      rng_session: rngSession,
      ui_animations_reference: ARMENIAN_BELOT_LOCAL_UI_ANIMATIONS,
      engine_state_full: serializeArmenianEngineStateForLog(engineState),
      public_snapshot_dto_debug_all_hands: publicSnapshot,
      command_history_every_step_full: history,
      ui_score_sheet_rows: scoreSheetRows,
      ui_last_round_summary: lastRoundSummary,
      ui_legal_plays_seat0_if_hero_turn: humanLegalPlays,
    };
  }, [engineState, history, scoreSheetRows, lastRoundSummary, humanLegalPlays]);

  const handleCopyState = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(stateJson);
      setCopyNotice('engine');
      window.setTimeout(() => setCopyNotice((n) => (n === 'engine' ? null : n)), 2200);
    } catch {
      setCopyNotice(null);
    }
  }, [stateJson]);

  const handleCopyFullReport = useCallback(async () => {
    try {
      const doc = { ...specialistFullReport, exportedAt: new Date().toISOString() };
      await navigator.clipboard.writeText(JSON.stringify(doc, null, 2));
      setCopyNotice('full');
      window.setTimeout(() => setCopyNotice((n) => (n === 'full' ? null : n)), 3200);
    } catch {
      setCopyNotice(null);
    }
  }, [specialistFullReport]);

  const handleDownload = useCallback(() => {
    const doc = { ...specialistFullReport, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `armenian-belot-full-report-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [specialistFullReport]);

  if (!open) return null;

  const tabs = [
    { id: 'state', label: 'Движок', icon: Braces },
    { id: 'snapshot', label: 'DTO', icon: Layers },
    { id: 'history', label: 'История', icon: ListOrdered },
  ];

  const panel = (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[180] bg-black/75 backdrop-blur-[2px]"
        aria-label="Закрыть отладку"
        onClick={onClose}
      />
      <div
        className={cn(
          'fixed z-[181] flex max-h-[min(92vh,720px)] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl border border-border/60 bg-card text-foreground shadow-[0_16px_48px_rgba(0,0,0,0.5)]',
          'inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl',
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="arm-engine-debug-title"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border/50 px-3 py-2.5 sm:px-4">
          <div className="flex min-w-0 items-center gap-2">
            <Braces className="h-5 w-5 shrink-0 text-cyan-400/90" aria-hidden />
            <div className="min-w-0">
              <h2 id="arm-engine-debug-title" className="truncate text-sm font-bold">
                Отладка движка
              </h2>
              <p className="truncate text-[10px] text-muted-foreground">Армянский белот · локально</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={handleCopyState}
              title="Только текущее состояние движка (JSON), без истории и DTO"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-muted/30 text-foreground transition hover:bg-muted/50"
            >
              <ClipboardCopy className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={handleDownload}
              title="Скачать тот же полный отчёт, что и «скопировать всю сводку»"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-muted/30 text-foreground transition hover:bg-muted/50"
            >
              <Download className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Очистить историю шагов в памяти браузера?')) onClearHistory();
              }}
              title="Очистить историю"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-muted/30 text-foreground transition hover:bg-muted/50"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/60 bg-muted/30 transition hover:bg-muted/50"
              aria-label="Закрыть"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>

        {copyNotice === 'engine' ? (
          <p className="border-b border-emerald-500/30 bg-emerald-950/30 px-3 py-1.5 text-center text-[11px] text-emerald-200">
            В буфер: только блок «состояние движка» (как на вкладке «Движок»)
          </p>
        ) : null}
        {copyNotice === 'full' ? (
          <p className="border-b border-emerald-500/30 bg-emerald-950/30 px-3 py-1.5 text-center text-[11px] text-emerald-200">
            Полная сводка скопирована — можно вставить в чат, тикет или файл
          </p>
        ) : null}

        <div className="border-b border-border/50 px-3 py-2.5 sm:px-4">
          <button
            type="button"
            onClick={handleCopyFullReport}
            title="Один JSON: движок + DTO + каждая команда с событиями и состоянием после шага + листок + итог раунда + допустимые ходы"
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-400/50 bg-gradient-to-b from-emerald-900/55 via-emerald-950/40 to-slate-950/50 py-3 text-xs font-bold uppercase tracking-wide text-emerald-50 shadow-[0_8px_28px_rgba(16,185,129,0.2)] transition hover:border-emerald-300/60 hover:from-emerald-800/60 active:scale-[0.99] sm:text-[13px]"
          >
            <Copy className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
            Скопировать всю сводку одним кликом
          </button>
          <p className="mt-2 text-center text-[10px] leading-snug text-muted-foreground">
            Максимально развёрнутый JSON (отступы 2 пробела): все разделы сразу, включая{' '}
            <span className="font-semibold text-emerald-200/90">ui_animations_reference</span> — для отправки
            специалистам. Кнопка «скачать» в шапке сохраняет тот же состав в файл.
          </p>
        </div>

        <div className="flex gap-1 border-b border-border/40 px-2 py-1.5 sm:px-3">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold transition sm:text-xs',
                tab === t.id
                  ? 'bg-cyan-500/20 text-cyan-100'
                  : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground',
              )}
            >
              <t.icon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
              {t.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2 sm:px-3 sm:py-3">
          {tab === 'state' ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-violet-500/35 bg-violet-950/20 px-2 py-2">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-violet-200">
                  <Sparkles className="h-3.5 w-3.5 shrink-0 text-violet-300" aria-hidden />
                  Анимации UI (локальный стол)
                </div>
                <p className="mt-1 text-[10px] leading-snug text-violet-100/85">
                  Ниже — когда эффект показывается, как выглядит, зачем нужен и чем реализован. Тот же список в
                  полной сводке: ключ <span className="font-mono text-violet-100">ui_animations_reference</span>.
                </p>
                <ul className="mt-2 max-h-[min(36vh,280px)] space-y-2 overflow-y-auto pr-0.5">
                  {ARMENIAN_BELOT_LOCAL_UI_ANIMATIONS.map((a) => (
                    <li
                      key={a.id}
                      className="rounded-md border border-white/10 bg-black/25 px-2 py-1.5 text-[10px] leading-snug text-slate-100"
                    >
                      <p className="font-semibold text-violet-100">{a.title}</p>
                      <p className="mt-0.5 text-slate-300/95">
                        <span className="font-semibold text-slate-400">Когда:</span> {a.trigger}
                      </p>
                      <p className="mt-0.5 text-slate-300/95">
                        <span className="font-semibold text-slate-400">Как выглядит:</span> {a.appearance}
                      </p>
                      <p className="mt-0.5 text-slate-300/95">
                        <span className="font-semibold text-slate-400">Зачем:</span> {a.purpose}
                      </p>
                      <p className="mt-0.5 font-mono text-[9px] text-cyan-200/85">{a.implementation}</p>
                    </li>
                  ))}
                </ul>
              </div>
              {humanLegalPlays && humanLegalPlays.length ? (
                <div className="rounded-lg border border-cyan-500/35 bg-cyan-950/20 px-2 py-2 text-[11px]">
                  <p className="font-semibold text-cyan-200">Допустимые ходы (место 0)</p>
                  <p className="mt-1 font-mono text-[10px] leading-relaxed text-cyan-100/90">
                    {humanLegalPlays.map((x) => x.label).join(' · ')}
                  </p>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    id: {humanLegalPlays.map((x) => x.id).join(', ')}
                  </p>
                </div>
              ) : null}
              {lastRoundSummary ? (
                <div className="rounded-lg border border-amber-500/30 bg-amber-950/15 px-2 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-200/90">
                    Последний итог раунда (UI)
                  </p>
                  <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] text-amber-50/95">
                    {JSON.stringify(lastRoundSummary, null, 2)}
                  </pre>
                </div>
              ) : null}
              {scoreSheetRows.length ? (
                <div className="rounded-lg border border-border/50 bg-muted/20 px-2 py-2">
                  <p className="text-[10px] font-semibold text-muted-foreground">Листок (строки UI)</p>
                  <pre className="mt-1 max-h-32 overflow-auto font-mono text-[10px]">
                    {JSON.stringify(scoreSheetRows, null, 2)}
                  </pre>
                </div>
              ) : null}
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Полное состояние движка (после последней команды)
              </p>
              <pre className="max-h-[min(48vh,420px)] overflow-auto rounded-lg border border-border/50 bg-black/40 p-2 font-mono text-[10px] leading-snug text-slate-100 sm:text-[11px]">
                {stateJson}
              </pre>
            </div>
          ) : null}

          {tab === 'snapshot' ? (
            <div className="space-y-2">
              <p className="text-[11px] text-muted-foreground">
                Снимок как для сети: <code className="text-foreground">buildPublicSnapshot</code>, руки всех мест (
                <code>debug_all</code>).
              </p>
              <pre className="max-h-[min(58vh,520px)] overflow-auto rounded-lg border border-border/50 bg-black/40 p-2 font-mono text-[10px] leading-snug text-slate-100 sm:text-[11px]">
                {publicSnapshotJson}
              </pre>
            </div>
          ) : null}

          {tab === 'history' ? (
            <div className="space-y-2">
              <p className="text-[11px] text-muted-foreground">
                Шагов в памяти: <span className="font-mono text-foreground">{history.length}</span> (старые отрезаются
                после 500).
              </p>
              <ul className="space-y-1.5">
                {[...history].reverse().map((row) => {
                  const openRow = expandedSeq === row.seq;
                  return (
                    <li
                      key={row.seq}
                      className={cn(
                        'rounded-lg border px-2 py-1.5 text-[11px]',
                        row.ok ? 'border-border/50 bg-muted/15' : 'border-red-500/40 bg-red-950/25',
                      )}
                    >
                      <button
                        type="button"
                        className="flex w-full flex-col items-start gap-0.5 text-left"
                        onClick={() => setExpandedSeq(openRow ? null : row.seq)}
                      >
                        <span className="font-mono text-[10px] text-muted-foreground">
                          #{row.seq} · {row.at}
                        </span>
                        <span className={cn('font-semibold', row.ok ? 'text-foreground' : 'text-red-200')}>
                          {row.ok ? (row.cmd && typeof row.cmd === 'object' && row.cmd.type) || '?' : 'ОШИБКА'}
                          {!row.ok && row.error ? `: ${row.error}` : null}
                        </span>
                        {Array.isArray(row.events) && row.events.length ? (
                          <span className="text-[10px] text-cyan-200/90">
                            События: {row.events.map((e) => (e && typeof e === 'object' && e.type) || '?').join(', ')}
                          </span>
                        ) : null}
                        <span className="text-[10px] text-muted-foreground">{openRow ? '▼' : '▶'} подробности</span>
                      </button>
                      {openRow ? (
                        <div className="mt-2 space-y-2 border-t border-border/40 pt-2">
                          <p className="text-[10px] font-semibold text-muted-foreground">Команда</p>
                          <pre className="max-h-40 overflow-auto rounded bg-black/35 p-1.5 font-mono text-[10px]">
                            {JSON.stringify(row.cmd, null, 2)}
                          </pre>
                          <p className="text-[10px] font-semibold text-muted-foreground">События</p>
                          <pre className="max-h-40 overflow-auto rounded bg-black/35 p-1.5 font-mono text-[10px]">
                            {JSON.stringify(row.events, null, 2)}
                          </pre>
                          {Array.isArray(row.eventSummaries) && row.eventSummaries.length ? (
                            <>
                              <p className="text-[10px] font-semibold text-muted-foreground">Кратко (тип + ключи payload)</p>
                              <pre className="max-h-32 overflow-auto rounded bg-black/35 p-1.5 font-mono text-[10px]">
                                {JSON.stringify(row.eventSummaries, null, 2)}
                              </pre>
                            </>
                          ) : null}
                          {row.diff && typeof row.diff === 'object' ? (
                            <>
                              <p className="text-[10px] font-semibold text-muted-foreground">Diff состояния</p>
                              <pre className="max-h-48 overflow-auto rounded bg-black/35 p-1.5 font-mono text-[10px]">
                                {JSON.stringify(row.diff, null, 2)}
                              </pre>
                            </>
                          ) : null}
                          {row.rng ? (
                            <>
                              <p className="text-[10px] font-semibold text-muted-foreground">RNG после шага</p>
                              <pre className="max-h-28 overflow-auto rounded bg-black/35 p-1.5 font-mono text-[10px]">
                                {JSON.stringify(row.rng, null, 2)}
                              </pre>
                            </>
                          ) : null}
                          {row.ui ? (
                            <>
                              <p className="text-[10px] font-semibold text-muted-foreground">UI (эвристика анимаций)</p>
                              <pre className="max-h-28 overflow-auto rounded bg-black/35 p-1.5 font-mono text-[10px]">
                                {JSON.stringify(row.ui, null, 2)}
                              </pre>
                            </>
                          ) : null}
                          {row.validation ? (
                            <>
                              <p className="text-[10px] font-semibold text-muted-foreground">Валидация</p>
                              <pre className="max-h-36 overflow-auto rounded bg-black/35 p-1.5 font-mono text-[10px]">
                                {JSON.stringify(row.validation, null, 2)}
                              </pre>
                            </>
                          ) : null}
                          <p className="text-[10px] font-semibold text-muted-foreground">Состояние после шага</p>
                          <pre className="max-h-48 overflow-auto rounded bg-black/35 p-1.5 font-mono text-[10px]">
                            {row.stateAfter != null
                              ? JSON.stringify(row.stateAfter, null, 2)
                              : 'null — полный снимок см. «Движок» / полная сводка (engine_state_full), шаг логируется через diff.'}
                          </pre>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              {history.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground">Пока нет записей — сделайте ход в игре.</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(panel, document.body);
  }
  return null;
}
